import React, { useState, useEffect } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { Button, Paper, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LeaveRegisterModal from './LeaveRegisterModal'
import { fetchLeave } from '../../../services/LeaveService'

const Status = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED'
}

const LeaveListLayout = () => {
    const navigate = useNavigate()
    const DEFAULT_PAGE_SIZE = 10
    const [dtr, setDTR] = useState([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false) // New state for submission loading
    const [selectionModel, setSelectionModel] = useState([])
    const [searchTerm, setSearchTerm] = useState(null)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const [refresh, setRefresh] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false)

    const columns = [
        { field: 'type', headerName: 'Type', flex: 1 },
        { field: 'reason', headerName: 'Reason', flex: 1 },
        { field: 'start', headerName: 'Start', flex: 1 },
        { field: 'end', headerName: 'End', flex: 1 },
        { field: 'internalRemarks', headerName: 'Internal Remarks', flex: 3 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const status = params.value
                let badgeClass = 'badge-neutral'
                let text = ''

                switch (status) {
                    case Status.PENDING:
                        badgeClass = 'badge-warning'
                        text = 'PENDING'
                        break
                    case Status.APPROVED:
                        badgeClass = 'badge-success'
                        text = 'APPROVED'
                        break
                    case Status.REJECTED:
                        badgeClass = 'badge-danger'
                        text = 'REJECTED'
                        break
                }

                return <span className={badgeClass}>{text}</span>
            }
        },
        { field: 'userAssigned', headerName: 'Assigned To', flex: 1 }
    ]

    useEffect(() => {
        fetchEmployeeList(searchTerm, page, pageSize)
    }, [searchTerm, page, pageSize, refresh])

    const fetchEmployeeList = async (search, page, limit) => {
        setLoading(true)
        try {
            const filter = { page, limit, search }
            let response = await fetchLeave(filter)
            const dailyRecords = response.data || []
            setTotalCount(response.count || 0)

            const formattedData = dailyRecords.map((record, index) => ({
                id: record.id || index,
                type: record.type,
                reason: record.reason,
                start: record.start,
                end: record.end,
                status: record.status,
                internalRemarks: record.internalRemarks,
                userAssigned: record.assignedUser
                    ? `${record.assignedUser.firstName} ${record.assignedUser.lastName}`
                    : record.assignedApproverRole
                      ? record.assignedApproverRole
                      : 'N/A'
            }))

            setDTR(formattedData)
        } catch (error) {
            console.error('Error fetching leave data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value)
    }

    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true)
    }

    const handleCloseRegisterDialog = () => {
        setRegisterDialogOpen(false)
    }

    const handleRowClick = (params) => {
        navigate(`/leave/details/${params.row.id}`)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            fetchEmployeeList(searchTerm, page, pageSize)
        }
    }

    const handleLeaveSubmission = async () => {
        setSubmitting(true)
        try {
            setRefresh((prev) => !prev)
        } catch (error) {
            console.error('Error submitting leave:', error)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="card-surface p-4 sm:p-6 w-full mt-4">
            {loading && <p className="text-sm text-gray-500 mb-2">Loading...</p>}
            {responseMessage && <p className="text-sm text-emerald-600 mb-2">{responseMessage}</p>}

            {/* Search and Register Button */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search..."
                    className="input-field sm:max-w-xs"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                />

                <button onClick={handleOpenRegisterDialog} className="btn-primary sm:ml-auto" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Apply Leave'}
                </button>
            </div>

            <Paper sx={{ width: '100%', height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 260px)' }, minWidth: 0 }}>
                <DataGrid
                    rows={dtr}
                    columns={columns}
                    loading={loading}
                    page={page - 1}
                    pageSize={pageSize}
                    rowCount={totalCount}
                    paginationMode="server"
                    initialState={{
                        pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } }
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    onPaginationModelChange={(newPaginationModel) => {
                        setPage(newPaginationModel.page + 1)
                        setPageSize(newPaginationModel.pageSize)
                    }}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    selectionModel={selectionModel}
                    onRowClick={handleRowClick}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'default'
                        }
                    }}
                />
            </Paper>

            <LeaveRegisterModal
                open={registerDialogOpen}
                onClose={handleCloseRegisterDialog}
                onRegisterSuccess={handleLeaveSubmission}
            />
        </div>
    )
}

export default LeaveListLayout
